import { useState, useEffect } from 'react';
import { deleteItem, post, get } from '../utils/fetch';
import snakeCaseKeys from 'snakecase-keys';
import camelCaseKeys from 'camelcase-keys';
import {
  CommentParams,
  UpdateComment,
  updateComment
} from '../../../clients/shared/updateComment';

export type Comment = {
  id: number;
  text: string;
  reactions: string;
  taskId: number;
};

type Props = {
  taskId: number | null;
  projectId: number;
  bugherdUrl: string;
  currentUser: any;
};

const useComments = ({
  taskId,
  projectId,
  bugherdUrl,
  currentUser
}: Props): {
  createComment: (commentBody: { isPrivate: boolean; text: string }) => void;
  deleteComment: (commentId: number) => void;
  updateComment: UpdateComment;
  comments: Comment[];
  loading: boolean;
  handlePusherUpdateForLocalComment: (updatedComment: Comment) => void;
} => {
  const [comments, _setComments] = useState<{
    [key: number]: Comment[] | undefined;
  }>({});
  const [loading, setLoading] = useState<boolean>(false);
  const taskComments = (taskId ? comments[taskId] || [] : []).slice();
  const commentEndpoint = `${bugherdUrl}/projects/${projectId}/tasks/${taskId}/comments`;

  const endpoint = (commentId?: number) =>
    `${commentEndpoint}${commentId ? `/${commentId}` : ''}`;

  const setComments = (newComments: Comment[]) => {
    _setComments(previousComments => {
      const initialComments = { ...previousComments };
      // @ts-expect-error
      initialComments[taskId] = newComments;
      return initialComments;
    });
  };

  const getComments = () => {
    setLoading(true);
    get(`${commentEndpoint}.json`)
      .then(response => {
        const _comments = camelCaseKeys(response, { deep: true });
        setComments(_comments);
        setLoading(false);
      })
      .catch(error => {
        console.error(error);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (taskId && projectId && bugherdUrl) getComments();
  }, [taskId, projectId, bugherdUrl]);

  const handlePusherUpdateForLocalComment = (updatedComment: Comment) => {
    _setComments(previousComments => {
      const initialComments = { ...previousComments };
      const commentsClone = updatedComment.taskId
        ? (initialComments[updatedComment.taskId] || []).slice()
        : [];
      const commentIndex = commentsClone.findIndex(
        ({ id, text }: Comment) =>
          id === updatedComment.id ||
          (id === -1 && updatedComment.text === text)
      );
      commentIndex === -1
        ? commentsClone.unshift(updatedComment)
        : commentsClone.splice(commentIndex, 1, updatedComment);
      initialComments[updatedComment.taskId] = commentsClone;
      return initialComments;
    });
  };

  const deleteComment = (commentId: number) => {
    const initialComments = comments;
    const commentsClone = taskComments;
    const commentsToSet = commentsClone.filter(
      ({ id }: Comment) => commentId !== id
    );
    setComments(commentsToSet);
    deleteItem(endpoint(commentId)).catch(error => {
      console.error(error);
      _setComments(initialComments);
    });
  };

  type CommentBody = {
    isPrivate: boolean;
    text: string;
  };

  const newCommentBody = ({ isPrivate, text }: CommentBody) => ({
    id: -1,
    isPrivate,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    text,
    userId: currentUser.id,
    user: currentUser,
    readByUsers: [currentUser.id],
    reactions: '{}',
    taskId: taskId || 0
  });

  const createComment = (commentBody: CommentBody) => {
    const initialComments = comments;
    const commentsClone = taskComments;
    const commentsToEdit = commentsClone.slice();
    commentsToEdit.unshift(newCommentBody(commentBody));
    setComments(commentsToEdit);

    post(endpoint(), snakeCaseKeys(commentBody, { deep: true }))
      .then(response => {
        const commentsCopy = taskComments.filter(
          ({ id }: Comment) => id !== -1
        );
        const incomingComment = camelCaseKeys(response, { deep: true });
        commentsCopy.push(incomingComment);
        setComments(commentsCopy);
      })
      .catch(error => {
        console.error(error);
        _setComments(initialComments);
      });
  };

  return {
    createComment,
    deleteComment,
    updateComment: ({ commentId, commentEvent }: CommentParams) =>
      updateComment({
        commentId,
        // @ts-expect-error
        setComments,
        comments: taskComments,
        endpoint: endpoint(commentId),
        commentEvent
      }),
    comments: taskComments,
    loading: loading && taskComments.length === 0,
    handlePusherUpdateForLocalComment
  };
};

export default useComments;
